import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowClassRules,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { LopAgGrid } from '../LopAgGrid';
import { LopGridCustomizationDialog } from '../LopGridCustomizationDialog';
import { useCrudGrid } from '../../hooks/useCrudGrid';
import { useGridCustomization } from '../../hooks/useGridCustomization';
import { LopGridToolbar, LopGridStatusBar } from '../LopGridToolbar';
import { exportGridCsv } from '../../utils/exportGridCsv';
import { useGridContextMenu, type UseGridContextMenuOptions } from '../../hooks/useGridContextMenu';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit as canEditUtil } from '../../utils/permissions';
import {
  applyCustomization,
  buildZebraRule,
  computeSummaryRow,
} from '../../utils/applyCustomization';
import type { UserCustomizationSettings } from '../../types/UserCustomization';
import type { LopMenuItem } from '../LopGridContextMenu';
import { t, type MessageKey } from '../../i18n/t';
import { useTabActive, useTabDirty } from '../../tabs';
import './LopCrudPage.css';

export interface LopCrudPageProps<T> {
  resourceCode: string;
  titleKey: MessageKey;
  idField: keyof T;
  getRows: () => Promise<T[]>;
  saveBatch: (added: T[], modified: T[], deletedIds: unknown[]) => Promise<T[]>;
  columnDefs: ColDef<T>[];
  newRowDefaults?: () => Partial<T>;
  // "Fetch-button screens": true until the user's first click on the fetch
  // button. While true: no getRows call (initial / refresh / auto-refresh),
  // and the grid shows a hint instead of "No Rows To Show".
  awaitingFetch?: boolean;
  // Label of the screen's own fetch button, interpolated into the hint.
  // Defaults to t('common.fetch').
  fetchLabel?: string;
  // Toolbar button visibility. Default true (backward-compatible). Update-only
  // pages (e.g. RolePermissions) set both false to hide Add + Delete.
  showAdd?: boolean;
  showDelete?: boolean;
  // Page-specific right-click items (see useGridContextMenu.extraItems). Must be
  // memoized by the caller.
  extraMenuItems?: (ctx: {
    rowData: Record<string, unknown> | null;
    colId: string | null;
    value: unknown;
    node: unknown;
  }) => LopMenuItem[];
}

type GridRow<T> = T & {
  __tempId: string;
  __status: 'added' | 'modified' | 'unchanged';
  __deleted: boolean;
};

export function LopCrudPage<T extends object>(props: LopCrudPageProps<T>) {
  const {
    resourceCode,
    titleKey,
    idField,
    getRows,
    saveBatch,
    columnDefs,
    newRowDefaults,
    showAdd = true,
    showDelete = true,
    extraMenuItems,
    awaitingFetch = false,
    fetchLabel,
  } = props;
  const awaitingHint = useMemo(
    () =>
      `<span class="lop-crud-page__await-hint">${t('common.awaitingFetchHint', {
        fetch: fetchLabel ?? t('common.fetch'),
      })}</span>`,
    [fetchLabel],
  );

  const { user } = useAuth();
  const canEdit = canEditUtil(user, resourceCode);
  const [searchParams] = useSearchParams();

  // Initial value from ?search= URL param (e.g. opened from FundForm "Manage
  // accounts" CTA). Not synced on URL changes — user can freely edit afterwards.
  const initialQuickFilter = useMemo(
    () => searchParams.get('search') ?? '',
    // Mount-only: subsequent URL changes (incl. user typing into the search
    // box) must not revert the input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    rowStates,
    loading,
    saving,
    error,
    hasChanges,
    addRow,
    updateRow,
    toggleDelete,
    save: saveRows,
    cancel: cancelRows,
    refresh: refreshRows,
  } = useCrudGrid<T>({
    getRows,
    saveBatch,
    idField,
    newRowDefaults,
    enabled: !awaitingFetch,
  });

  // Manual row colors (right-click "צבע שורה") are ephemeral: every reload /
  // save / cancel wipes them. clearRowColors comes from useGridContextMenu,
  // which is declared further down — reach it through a ref.
  const clearRowColorsRef = useRef<() => void>(() => {});
  const refresh = useCallback(async () => {
    clearRowColorsRef.current();
    await refreshRows();
  }, [refreshRows]);
  const save = useCallback(async () => {
    clearRowColorsRef.current();
    await saveRows();
  }, [saveRows]);
  const cancel = useCallback(() => {
    clearRowColorsRef.current();
    cancelRows();
  }, [cancelRows]);

  // Internal tabs (plan §3.6): report unsaved changes to this page's OWN tab
  // (dirty dot + close/logout/beforeunload confirmation), and know whether
  // this tab is the currently-visible one (used below to pause auto-refresh
  // while hidden). Both no-op outside a tab pane.
  useTabDirty(hasChanges);
  const isTabActive = useTabActive();

  const gridApiRef = useRef<GridApi<GridRow<T>> | null>(null);

  // Per-user customization (explicit-save model, v0.1.84): `customization` is
  // what is on screen (saved + unsaved changes); only the status-bar button
  // persists. See useGridCustomization.
  const {
    customization,
    dirty: customizationDirty,
    toastVisible,
    onGridReady: onCustomizationGridReady,
    onColumnMoved,
    onColumnResized,
    onSortChanged,
    snapshot: snapshotCustomization,
    applyDraft: applyCustomizationDraft,
    quickCustomize,
    reset: resetCustomization,
    saveCustomizationNow,
  } = useGridCustomization(resourceCode, gridApiRef);

  const [selectedCount, setSelectedCount] = useState(0);
  const [quickFilter, setQuickFilter] = useState(initialQuickFilter);

  // Internal tabs (plan §3.6/§4): re-focusing an ALREADY-open tab whose
  // frozen location gained a new/changed `?search=` (e.g. FundAccountsPanel's
  // "manage accounts" CTA on a different fund) must re-apply the new filter —
  // `initialQuickFilter` above is mount-only by design so the user can freely
  // clear/retype afterwards, so this is a SEPARATE effect that only fires
  // when the param itself changes after mount (not on every render).
  const currentSearchParam = searchParams.get('search');
  const lastAppliedSearchParamRef = useRef(currentSearchParam);
  useEffect(() => {
    if (currentSearchParam !== lastAppliedSearchParamRef.current) {
      lastAppliedSearchParamRef.current = currentSearchParam;
      if (currentSearchParam) setQuickFilter(currentSearchParam);
    }
  }, [currentSearchParam]);

  const [displayedRowCount, setDisplayedRowCount] = useState(0);
  const [pendingFocusTempId, setPendingFocusTempId] = useState<string | null>(null);
  const [customizationOpen, setCustomizationOpen] = useState(false);
  // Customization as it was (incl. live layout) when the dialog was opened.
  const [dialogCurrent, setDialogCurrent] = useState<UserCustomizationSettings | null>(null);
  // Sample row passed into the dialog so the preview reflects the cell the
  // user right-clicked on. Captured at menu-open time, cleared on dialog close.
  const [dialogSampleRow, setDialogSampleRow] = useState<Record<string, unknown> | null>(null);
  // Column id the user right-clicked on — passed to the dialog so it opens
  // on the columns tab with that column focused. Cleared on dialog close.
  const [dialogInitialField, setDialogInitialField] = useState<string | null>(null);
  const pending = useMemo(() => {
    const added = rowStates.filter((r) => r.status === 'added' && !r.isDeleted).length;
    const modified = rowStates.filter((r) => r.status === 'modified' && !r.isDeleted).length;
    const deleted = rowStates.filter((r) => r.isDeleted).length;
    return { added, modified, deleted };
  }, [rowStates]);

  const totalCount = rowStates.length;
  const isFiltered = quickFilter.length > 0 && displayedRowCount < totalCount;

  const gridData = useMemo<GridRow<T>[]>(
    () =>
      rowStates.map((s) => ({
        ...(s.data as object),
        __tempId: s.tempId,
        __status: s.status,
        __deleted: s.isDeleted,
      })) as GridRow<T>[],
    [rowStates],
  );

  // Apply customization: reorder columns + layer styling/format/sign-coloring
  // on top of the page's base columnDefs. The original ColDefs are untouched.
  const effectiveColumnDefs = useMemo<ColDef<GridRow<T>>[]>(() => {
    const merged = applyCustomization(columnDefs, customization);
    return merged as unknown as ColDef<GridRow<T>>[];
  }, [columnDefs, customization]);

  // Memoize defaultColDef so AgGrid doesn't see a "new" object every React
  // render. AgGrid v35 resets column state (including user-resized widths and
  // the flex configuration) whenever it sees a new defaultColDef reference —
  // which used to happen every render when this was an inline {editable: ...}
  // literal. The drag-to-resize bug we saw on /bfix-rate-types is exactly that:
  // AgGrid would receive the resize event, then on the next render it would
  // see "new" defaultColDef and snap the column back to flex:1 (the default
  // from LopAgGrid's lopDefaultColDef). Memoizing on `canEdit` only.
  const memoDefaultColDef = useMemo<ColDef<GridRow<T>>>(
    () => ({ editable: canEdit }),
    [canEdit],
  );

  // Filter-aware summary trigger — bumps on every AgGrid model update so the
  // pinned-bottom memo re-reads AgGrid's filtered+sorted rows after each
  // filter/sort change. (Community grids don't fire a dedicated "post-filter"
  // event we can hook directly; onModelUpdated catches it.)
  const [filterVersion, setFilterVersion] = useState(0);

  // Pinned bottom summary row. Reads `forEachNodeAfterFilterAndSort` so it
  // reflects ONLY the rows the user currently sees (quickFilter / column
  // filters / sort). Computed in JS (Community); when Enterprise is purchased,
  // replace with native grandTotalRow + agAggregationComponent (filter-aware
  // natively, no client-side recompute).
  const pinnedBottomRowData = useMemo(() => {
    const api = gridApiRef.current;
    if (!api) return undefined;
    const rows: T[] = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (!node.data || node.data.__deleted) return;
      const { __tempId, __status, __deleted, ...rest } = node.data;
      rows.push(rest as unknown as T);
    });
    const summary = computeSummaryRow(rows, customization, columnDefs);
    return summary ? [summary] : undefined;
    // filterVersion is the trigger; rowStates is included so adds/edits also
    // recompute (the AgGrid model update fires for those too, but we depend
    // explicitly to be safe).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVersion, customization, columnDefs, rowStates]);

  // Merge zebra-striping rule with the existing status-based rules.
  const rowClassRules = useMemo<RowClassRules<GridRow<T>>>(() => {
    const base: RowClassRules<GridRow<T>> = {
      'lop-crud-row-added': (p: RowClassParams<GridRow<T>>) =>
        p.data?.__status === 'added' && !p.data?.__deleted,
      'lop-crud-row-modified': (p: RowClassParams<GridRow<T>>) =>
        p.data?.__status === 'modified' && !p.data?.__deleted,
      'lop-crud-row-deleted': (p: RowClassParams<GridRow<T>>) =>
        p.data?.__deleted === true,
    };
    const zebra = buildZebraRule(customization);
    if (zebra) {
      Object.assign(base, zebra);
    }
    return base;
  }, [customization]);

  // Auto-refresh: fires the existing useCrudGrid.refresh() every N seconds when
  // configured. Pauses while the user has unsaved changes (banking safety —
  // we never wipe an in-progress edit), while saving, and — internal tabs
  // (plan §3.6) — while this tab isn't the visible one (no point refreshing a
  // hidden grid; it also avoids surprising the user with rows that shifted
  // under them the instant they switch back).
  useEffect(() => {
    const seconds = customization?.grid.autoRefreshSeconds ?? 0;
    if (!seconds || seconds <= 0) return;
    const intervalMs = seconds * 1000;
    const id = setInterval(() => {
      if (!isTabActive) return;
      if (hasChanges || saving) return;
      void refresh();
    }, intervalMs);
    return () => clearInterval(id);
  }, [customization, hasChanges, saving, refresh, isTabActive]);

  // Refresh once, immediately, the moment this tab becomes active again —
  // only when auto-refresh is actually configured (a page with no configured
  // interval never auto-refreshes at all, and reactivation shouldn't change
  // that).
  const wasTabActiveRef = useRef(isTabActive);
  useEffect(() => {
    const seconds = customization?.grid.autoRefreshSeconds ?? 0;
    const becameActive = isTabActive && !wasTabActiveRef.current;
    wasTabActiveRef.current = isTabActive;
    if (seconds > 0 && becameActive && !hasChanges && !saving) {
      void refresh();
    }
  }, [isTabActive, customization, hasChanges, saving, refresh]);

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<GridRow<T>>) => {
      if (!event.data) return;
      // Read the post-edit row from event.data — not event.newValue. AgGrid mutates
      // event.data either via its default write (newValue → data[field]) or via a
      // custom valueSetter that may transform the value (e.g. dropdown label → id).
      // Spreading event.data honors both paths; event.newValue drops the
      // valueSetter's transformation.
      const { __tempId, __status, __deleted, ...rest } = event.data;
      updateRow(__tempId, rest as unknown as T);
    },
    [updateRow],
  );

  const handleCellKeyDown = useCallback(
    (event: CellKeyDownEvent<GridRow<T>>) => {
      const kbd = event.event as KeyboardEvent | null;
      if (!kbd || kbd.key !== 'Delete') return;
      if (!canEdit) return;
      const selected = event.api.getSelectedNodes();
      selected.forEach((node) => {
        if (node.data) toggleDelete(node.data.__tempId);
      });
    },
    [canEdit, toggleDelete],
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent<GridRow<T>>) => {
      gridApiRef.current = event.api;
      onCustomizationGridReady(event);
    },
    [onCustomizationGridReady],
  );

  // Strip our internal row fields before they reach the context-menu hook
  // (copy/paste/export/navigate must never see __tempId/__status/__deleted).
  const stripRowInternals = useCallback(
    (row: Record<string, unknown>) => {
      const { __tempId, __status, __deleted, ...rest } = row as {
        __tempId?: unknown;
        __status?: unknown;
        __deleted?: unknown;
      } & Record<string, unknown>;
      void __tempId; void __status; void __deleted;
      return rest;
    },
    [],
  );

  // Paste handler — gated on the page's edit permission. Wired only when
  // canEdit, so the menu item is hidden entirely for view-only users (mirrors
  // the prior "disabled" treatment but cleaner: no dead item in the menu).
  // Uses node.setDataValue so the change flows through onCellValueChanged →
  // useCrudGrid dirty tracking, same as before.
  const handlePaste = useMemo(
    () =>
      canEdit
        ? (colId: string, value: string, node: { setDataValue: (k: string, v: unknown) => void }) => {
            node.setDataValue(colId, value);
          }
        : undefined,
    [canEdit],
  );

  // Customize handler — opens the dialog with the right row + column focused.
  const handleCustomize = useCallback(
    (sampleRow: Record<string, unknown> | null, colId: string | null) => {
      setDialogCurrent(snapshotCustomization());
      setDialogSampleRow(sampleRow);
      setDialogInitialField(colId);
      setCustomizationOpen(true);
    },
    [snapshotCustomization],
  );

  // Right-click "עיצוב מהיר": applied on screen + marks the customization
  // dirty (the status-bar button persists).
  const getColumnCustomization = useCallback(
    (colId: string) => customization?.columns[colId],
    [customization],
  );
  const handleQuickCustomize = quickCustomize;

  const { onCellContextMenu, contextMenu, getRowStyle, clearRowColors } = useGridContextMenu<GridRow<T>>({
    gridApiRef,
    columnDefs: columnDefs as ColDef<GridRow<T>>[],
    resourceCode,
    titleKey,
    stripRowInternals,
    onPaste: handlePaste,
    onCustomize: handleCustomize,
    onQuickCustomize: handleQuickCustomize,
    getColumnCustomization,
    extraItems: extraMenuItems as UseGridContextMenuOptions<GridRow<T>>['extraItems'],
  });

  useEffect(() => {
    clearRowColorsRef.current = clearRowColors;
  }, [clearRowColors]);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<GridRow<T>>) => {
      const count = event.api
        .getSelectedNodes()
        .filter((n) => n.data && !n.data.__deleted).length;
      setSelectedCount(count);
    },
    [],
  );

  const handleBulkDelete = useCallback(() => {
    if (!canEdit || !gridApiRef.current) return;
    const selected = gridApiRef.current.getSelectedNodes();
    selected.forEach((node) => {
      if (node.data && !node.data.__deleted) toggleDelete(node.data.__tempId);
    });
  }, [canEdit, toggleDelete]);

  const handleAdd = useCallback(() => {
    setPendingFocusTempId(addRow());
  }, [addRow]);

  const handleModelUpdated = useCallback(() => {
    if (gridApiRef.current) {
      setDisplayedRowCount(gridApiRef.current.getDisplayedRowCount());
    }
    // Bump the summary trigger so the pinned bottom row recomputes against
    // the latest filter/sort state.
    setFilterVersion((v) => v + 1);
    // After AgGrid applies the new rowData, scroll the freshly added row into
    // view and start editing it. getRowNode uses the __tempId id space (see
    // getRowId), which equals the RowState tempId returned by addRow.
    if (pendingFocusTempId && gridApiRef.current) {
      const api = gridApiRef.current;
      const node = api.getRowNode(pendingFocusTempId);
      if (node && node.rowIndex != null) {
        api.ensureNodeVisible(node, 'top');
        // First column that is editable for THIS row (handles editable
        // callbacks, e.g. PK editable only on added rows). Skips the selection
        // column, which is not editable.
        const editableCol = api.getColumns()?.find((c) => c.isCellEditable(node));
        if (editableCol) {
          api.setFocusedCell(node.rowIndex, editableCol);
          api.startEditingCell({
            rowIndex: node.rowIndex,
            colKey: editableCol.getColId(),
          });
        }
        setPendingFocusTempId(null);
      }
    }
  }, [pendingFocusTempId]);

  const handleExportCsv = useCallback(() => {
    exportGridCsv(gridApiRef.current, resourceCode);
  }, [resourceCode]);

  return (
    <div className="lop-crud-page">
      <header>
        <h1>{t(titleKey)}</h1>
        <LopGridToolbar
          quickFilter={quickFilter}
          onQuickFilterChange={setQuickFilter}
          showAdd={showAdd}
          showDelete={showDelete}
          addDisabled={!canEdit || saving}
          deleteDisabled={!canEdit || saving || selectedCount === 0}
          saveDisabled={!hasChanges || !canEdit || saving}
          cancelDisabled={!hasChanges || saving}
          exportDisabled={saving}
          onAdd={handleAdd}
          onDelete={handleBulkDelete}
          onSave={() => save()}
          onCancel={cancel}
          onExport={handleExportCsv}
        />
      </header>
      {error && (
        <div className="lop-crud-page__error">
          {t('crud.errorPrefix', { message: error })}
        </div>
      )}
      <div className="lop-crud-page__grid">
        {loading && rowStates.length === 0 && (
          <GridSkeleton columnCount={effectiveColumnDefs.length} />
        )}
        <LopAgGrid<GridRow<T>>
          rowData={gridData}
          columnDefs={effectiveColumnDefs}
          getRowId={(params: GetRowIdParams<GridRow<T>>) => params.data.__tempId}
          defaultColDef={memoDefaultColDef}
          rowSelection={{ mode: 'multiRow' }}
          rowClassRules={rowClassRules}
          getRowStyle={getRowStyle}
          pinnedBottomRowData={pinnedBottomRowData}
          onCellValueChanged={handleCellValueChanged}
          onCellKeyDown={handleCellKeyDown}
          onCellContextMenu={onCellContextMenu}
          onColumnMoved={onColumnMoved}
          onColumnResized={onColumnResized}
          onSortChanged={onSortChanged}
          // Suppress the browser's native right-click menu so our custom menu
          // isn't immediately overwritten by it. (onCellContextMenu's own
          // preventDefault is unreliable across browsers; this grid option is the
          // canonical fix.)
          preventDefaultOnContextMenu={true}
          onGridReady={handleGridReady}
          onSelectionChanged={handleSelectionChanged}
          onModelUpdated={handleModelUpdated}
          quickFilterText={quickFilter}
          loading={loading}
          overlayNoRowsTemplate={awaitingFetch ? awaitingHint : undefined}
        />
      </div>
      <LopGridStatusBar
        totalCount={totalCount}
        displayedCount={displayedRowCount}
        isFiltered={isFiltered}
        selectedCount={selectedCount}
        customizationDirty={customizationDirty}
        onSaveCustomization={() => saveCustomizationNow()}
        customizationToastVisible={toastVisible}
      >
        {hasChanges && (
          <div className="lop-crud-page__statusbar-changes">
            {pending.added > 0 && (
              <span
                className="lop-crud-page__statusbar-added"
                title={t('crud.statusBar.tooltipAdded')}
              >
                +{pending.added}
              </span>
            )}
            {pending.modified > 0 && (
              <span
                className="lop-crud-page__statusbar-modified"
                title={t('crud.statusBar.tooltipModified')}
              >
                ✎{pending.modified}
              </span>
            )}
            {pending.deleted > 0 && (
              <span
                className="lop-crud-page__statusbar-deleted"
                title={t('crud.statusBar.tooltipDeleted')}
              >
                −{pending.deleted}
              </span>
            )}
          </div>
        )}
      </LopGridStatusBar>
      {contextMenu}
      {customizationOpen && (
        <LopGridCustomizationDialog
          resourceCode={resourceCode}
          baseColumnDefs={columnDefs as ColDef<any>[]}
          current={dialogCurrent}
          sampleRowData={dialogSampleRow}
          initialSelectedField={dialogInitialField}
          onSave={applyCustomizationDraft}
          onReset={resetCustomization}
          onClose={() => {
            setCustomizationOpen(false);
            setDialogCurrent(null);
            setDialogSampleRow(null);
            setDialogInitialField(null);
          }}
        />
      )}
    </div>
  );
}

// Lightweight shimmer placeholder shown during the first data fetch, so the
// screen feels alive immediately on navigation instead of waiting for AgGrid
// to mount + overlay its own spinner. Auto-hides as soon as rows arrive.
function GridSkeleton({ columnCount }: { columnCount: number }) {
  const cols = Math.max(3, Math.min(columnCount || 6, 8));
  const rows = 10;
  return (
    <div
      className="lop-crud-page__skeleton"
      role="status"
      aria-live="polite"
      aria-label={t('crud.loading')}
    >
      <div className="lop-crud-page__skeleton-header">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="lop-crud-page__skeleton-cell lop-crud-page__skeleton-cell--header" />
        ))}
      </div>
      <div className="lop-crud-page__skeleton-body">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="lop-crud-page__skeleton-row">
            {Array.from({ length: cols }).map((_, c) => (
              <div key={c} className="lop-crud-page__skeleton-cell" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
